"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Send } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"] as const;
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"] as const;
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"] as const;
export default function EstimateRequest() {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [projectType] = useState("Deck");
  const [preferredTiming, setPreferredTiming] = useState("");
  const [budgetRange, setBudgetRange] = useState("");
  const [projectDetails, setProjectDetails] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.trim();
    setZipCode(val);
    if (zipError && /^\d{5}$/.test(val)) {
      setZipError("");
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("idle");
    setErrorMessage("");
    let hasError = false;
    if (!/^\d{5}$/.test(zipCode)) {
      setZipError("Please enter a valid 5-digit US ZIP code.");
      hasError = true;
    } else {
      setZipError("");
    }
    if (!consent) {
      setConsentError("Please agree to be contacted to proceed.");
      hasError = true;
    } else {
      setConsentError("");
    }
    if (hasError) {
      return;
    }
    setIsSubmitting(true);
    try {
      const pageUrl = typeof window !== "undefined" ? window.location.href : "";
      const bodyParams = new URLSearchParams({
        name: fullName,
        phone,
        email,
        project_type: projectType,
        address: zipCode,
        zip: zipCode,
        details: projectDetails,
        timing: preferredTiming,
        budget: budgetRange,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: pageUrl,
        form_location: "deck-remodeling-page"
      });
      if (!LEAD_WEBHOOK_URL) {
        throw new Error("Webhook endpoint unconfigured");
      }
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: bodyParams
      });
      if (response.ok) {
        setSubmitStatus("success");
      } else {
        setSubmitStatus("error");
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setSubmitStatus("error");
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-background text-foreground overflow-hidden">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl border border-border/80 bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 md:p-12 relative">
          <div className="mb-8 text-center sm:text-left">
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">
              Get Started
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-serif text-foreground font-normal tracking-tight">
              Request your project estimate
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Tell us about your outdoor living vision and our construction team will prepare a tailored consultation.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitStatus === "success" ? <motion.div key="success" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="py-12 text-center space-y-4">
                <div className="inline-flex items-center justify-center size-14 rounded-full bg-primary/10 text-primary mb-2">
                  <CheckCircle2 className="size-8" />
                </div>
                <h3 className="text-2xl font-serif text-foreground">Thank you for your request</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  We have received your project details and will be in touch shortly to review your estimate.
                </p>
              </motion.div> : <form key="form" onSubmit={handleSubmit} className="space-y-6" noValidate>
                {submitStatus === "error" && errorMessage && <Alert variant="destructive" className="border border-destructive/20 bg-destructive/5 text-destructive">
                    <AlertTriangle className="size-4" />
                    <AlertTitle>Unable to submit form</AlertTitle>
                    <AlertDescription className="text-destructive">
                      {errorMessage}
                    </AlertDescription>
                  </Alert>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="fullName" name="name" type="text" required placeholder="Jane Doe" value={fullName} onChange={e => setFullName(e.target.value)} className="bg-background/60 focus:bg-background h-10 border-border" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="phoneNumber" name="phone" type="tel" required placeholder="(470) 000-0000" value={phone} onChange={e => setPhone(e.target.value)} className="bg-background/60 focus:bg-background h-10 border-border" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="emailAddress" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="emailAddress" name="email" type="email" required placeholder="jane@example.com" value={email} onChange={e => setEmail(e.target.value)} className="bg-background/60 focus:bg-background h-10 border-border" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zipCode" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="zipCode" name="zip" type="text" required placeholder="30319" maxLength={5} value={zipCode} onChange={handleZipChange} aria-invalid={!!zipError} className="bg-background/60 focus:bg-background h-10 border-border" />
                    {zipError && <p className="text-xs text-destructive font-medium mt-1">
                        {zipError}
                      </p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="projectType" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <select id="projectType" name="project_type" value={projectType} disabled aria-readonly="true" className="flex h-10 w-full rounded-md border border-border bg-muted/60 px-3 py-2 text-sm text-foreground shadow-xs cursor-not-allowed outline-none">
                      {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredTiming" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      Preferred Timing
                    </Label>
                    <select id="preferredTiming" name="timing" value={preferredTiming} onChange={e => setPreferredTiming(e.target.value)} className="flex h-10 w-full rounded-md border border-border bg-background/60 focus:bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50">
                      <option value="">Select timeline</option>
                      {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="budgetRange" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                      Budget Range
                    </Label>
                    <select id="budgetRange" name="budget" value={budgetRange} onChange={e => setBudgetRange(e.target.value)} className="flex h-10 w-full rounded-md border border-border bg-background/60 focus:bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50">
                      <option value="">Select budget</option>
                      {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="projectDetails" className="text-xs uppercase tracking-wide text-foreground/90 font-medium">
                    Project Details / Message
                  </Label>
                  <Textarea id="projectDetails" name="details" placeholder="Tell us about the scope, deck square footage, composite vs wood preferences, or any specific ideas..." value={projectDetails} onChange={e => setProjectDetails(e.target.value)} className="min-h-28 bg-background/60 focus:bg-background border-border text-sm" />
                </div>

                <div className="space-y-1.5 pt-1">
                  <div className="flex items-start gap-3">
                    <Checkbox id="consentCheckbox" checked={consent} onCheckedChange={checked => {
                  const isChecked = checked === true;
                  setConsent(isChecked);
                  if (isChecked && consentError) setConsentError("");
                }} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                    <Label htmlFor="consentCheckbox" className="text-xs sm:text-sm text-muted-foreground leading-normal font-normal cursor-pointer select-none">
                      I agree to be contacted about my project by phone, text, or email.
                    </Label>
                  </div>
                  {consentError && <p className="text-xs text-destructive font-medium pl-7">
                      {consentError}
                    </p>}
                </div>

                <div className="pt-2">
                  <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-11 px-8 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                    {isSubmitting ? "Submitting..." : <>
                        Request Estimate
                        <Send className="size-4 ml-1" />
                      </>}
                  </Button>
                </div>
              </form>}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>;
}